import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Pagination from 'components/ui/pagination';

import OptionsFilters from './document_index/options_filters';
import DocTemplateTable from './document_index/doc_template_table';
import TemplateAccessReportModal from './template_access_report_modal';

import { deleteRequest, get, post, put } from 'helpers/api';
import confirm from 'components/confirm';
import DuplicateModal from './duplicate_modal';

export default function DocTemplateList({
  apiEndpoint,
  duplicateTemplateUrl,
  teamsAccess
}) {
  const [documentTemplates, setDocumentTemplates] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [searchText, setSearchText] = React.useState('');
  const [documentTypeFilter, setDocumentTypeFilter] = React.useState([]);
  const [createdByFilter, setCreatedByFilter] = React.useState([]);
  const [duplicateData, setDuplicateData] = React.useState({ show: false, template: {} });
  const [duplicateSaveError, setDuplicateSaveError] = React.useState(null);
  const [sortState, setSortState] = React.useState({ name: 'name', direction: 'asc' });
  const [templateReport, setTemplateReport] = React.useState(null);

  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [documentTypeOptions, setDocumentTypeOptions] = React.useState([]);
  const [createdByOptions, setCreatedByOptions] = React.useState([]);
  const [debouncedSearchText, setDebouncedSearchText] = React.useState('');

  const fetchTemplates = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await get(apiEndpoint, {
        params: {
          page: currentPage,
          search: debouncedSearchText || undefined,
          documentType: documentTypeFilter.length > 0 ? documentTypeFilter : undefined,
          createdBy: createdByFilter.length > 0 ? createdByFilter : undefined,
          sortBy: sortState.name,
          sortDirection: sortState.direction
        }
      });
      setDocumentTemplates(response.records);

      if (response.meta) {
        setTotalPages(response.meta.totalPages);
      }

      if (response.filterOptions) {
        setDocumentTypeOptions(response.filterOptions.documentTypes || []);
        setCreatedByOptions(response.filterOptions.createdBy || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setDocumentTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [apiEndpoint, currentPage, debouncedSearchText, documentTypeFilter, createdByFilter, sortState]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchText !== debouncedSearchText) {
        setDebouncedSearchText(searchText);
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText, debouncedSearchText]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleUpdateSearchText = (e) => {
    setSearchText(e.target.value);
  };

  const handleSearchClear = () => {
    setSearchText('');
    setCurrentPage(1);
  };

  const handleSort = (sortKey, direction) => {
    setSortState({ name: sortKey, direction });
    setCurrentPage(1);
  };

  const handleDocumentTypeChange = (newFilter) => {
    setDocumentTypeFilter(newFilter);
    setCurrentPage(1);
  };

  const handleCreatedByChange = (newFilter) => {
    setCreatedByFilter(newFilter);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDelete = (template) => {
    const message = template.createdByName ?
      `Are you sure you want to delete this template created by ${template.createdByName}?` :
      'Are you sure?';

    confirm(message, {
      buttonLabel: 'Delete Document Template',
      buttonStyle: 'danger',
      title: `Delete "${template.name}"?`
    }).then(() => {
      deleteRequest(template.deletePath).then(() => {
        fetchTemplates();
      });
    }).catch(() => {
      // noop
    });
  };

  const handleSetDefault = async (template, transactionType, enabled) => {
    try {
      await put(template.setDefaultProposalPath, { transactionType, enabled });
      fetchTemplates();
    } catch (error) {
      console.error('Error updating default proposal template:', error);
    }
  };

  const handleDuplicateOpen = (template) => {
    setDuplicateData({ show: true, template });
  };
  const handleDuplicateClose = () => {
    setDuplicateData({ show: false, template: {} });
    setDuplicateSaveError(null);
  };
  const handleDuplicateSave = ({ duplicateId, newName }) => {
    post(duplicateTemplateUrl, { duplicateId, newName }).then((resp) => {
      if (resp.success) {
        handleDuplicateClose();
        window.location.href = resp.editPath;
      } else {
        setDuplicateSaveError('Sorry, something went wrong when duplicating');
      }
    }).catch((e) => {
      setDuplicateSaveError(e || 'Sorry, an unexpected error occurred when duplicating');
    });
  };

  const pageHeader = <h4 className="mb-4">Document Defaults</h4>;

  if (loading) {
    return (
      <div>
        {pageHeader}
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {pageHeader}
      <OptionsFilters
        createdByFilter={createdByFilter}
        createdByOptions={createdByOptions}
        documentTypeFilter={documentTypeFilter}
        documentTypeOptions={documentTypeOptions}
        searchText={searchText}
        onCreatedByChange={handleCreatedByChange}
        onDocumentTypeChange={handleDocumentTypeChange}
        onSearchChange={handleUpdateSearchText}
        onSearchClear={handleSearchClear}
      />

      {documentTemplates.length > 0 ? (
        <div>
          <DocTemplateTable
            sortState={sortState}
            templates={documentTemplates}
            onAccessTemplateReport={teamsAccess && (template => setTemplateReport(template))}
            onDelete={handleDelete}
            onDuplicate={handleDuplicateOpen}
            onSetDefault={handleSetDefault}
            onSort={handleSort}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onChange={handlePageChange}
          />

          <DuplicateModal
            existingTemplates={documentTemplates}
            saveErrorMessage={duplicateSaveError}
            show={duplicateData.show}
            template={duplicateData.template}
            onHide={handleDuplicateClose}
            onSave={handleDuplicateSave}
          />

          {templateReport && (
            <TemplateAccessReportModal
              documentTemplate={templateReport}
              onHide={() => setTemplateReport(null)}
            />
          )}
        </div>
      ) : (
        <h6 className="mt-7">No Documents Found</h6>
      )}
    </div>
  );
}

DocTemplateList.propTypes = {
  apiEndpoint: PropTypes.string.isRequired,
  duplicateTemplateUrl: PropTypes.string,
  teamsAccess: PropTypes.bool
};
